import { useMemo } from 'react';
import { atomFamily, selectorFamily, atom, useRecoilValue, DefaultValue } from 'recoil';
import type { RecoilState, RecoilValueReadOnly } from 'recoil';
import type {
  ViewportsConfigurations,
  ViewportConfiguration,
  StudyGroup,
  ViewportDisplayConfiguration,
  DehydratedViewportsConfigurations,
} from './types';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
  localForagePersisterEffect,
} from 'utils/recoilEffects';
import { generateViewportsConfigurations } from './factories';
import { isPreviewingHangingProtocolState } from '../HangingProtocol/state';
import {
  layoutsForAllCasesState,
  DEFAULT_LAYOUTS,
} from 'domains/viewer/Viewer/StudyLoader/viewerLoaderState';
import { openedViewersState } from 'domains/viewer/TrackOpenedViewers';
import { useStudies } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { activeViewportState } from 'config/recoilState';
import {
  dehydrateViewportsConfigurations,
  hydrateViewportConfiguration,
  hydrateViewportsConfigurations,
} from './manipulators';
import { NOT_FOUND_CASE_ID, type ViewportTypeKeys } from 'config/constants';
import equals from 'ramda/es/equals';
import type { VtkAnnotation, Segment } from '../ViewportDre/Annotations/types';
import type { Layouts } from '../Viewer/StudyLoader/viewerLoaderState';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { env } from 'config/env';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import type { ScaleFactorTags } from '../ViewportDre/utils/math';
import { ViewType } from 'generated/graphql';
import { WithApolloContext } from 'modules/Apollo/types';

type ViewportsConfigurationsState = {
  created: Nullable<number>;
  updated: Nullable<number>;
  data: Nullable<DehydratedViewportsConfigurations>;
};

type GeneratorArgs = {
  defaults?: Partial<ViewportConfiguration>;
  groupedStudies: StudyGroup[];
};

type ViewportsConfigurationsSelectorArgs = Readonly<{
  caseSmid: Nullable<string>;
  generatorArgs: GeneratorArgs;
  toJSON: () => Record<any, any> | string;
}>;

type ViewportConfigurationSelectorArgs = Readonly<{
  caseSmid: Nullable<string>;
  viewportId: string;
  generatorArgs: GeneratorArgs;
  toJSON: () => Record<any, any> | string;
}>;

/**
 * This state is responsible of storing any user-defined viewport configurations.
 * We don't need to store anything that is automatically generated since that's
 * predictable and can be regenerated from the data.
 * This atom also includes two timestamps (created and updated) to help us
 * delete old configurations that are no longer needed.
 *
 * TODO: implement logic to automatically purge old configurations.
 */
// @ts-expect-error [EN-10263] - TS2322 - Type '(param: string) => RecoilState<{ created: number; updated: null; data: {}; }>' is not assignable to type '(caseSmid: string) => RecoilState<ViewportsConfigurationsState>'.
export const viewportsConfigurationsState: (
  caseSmid: string
) => RecoilState<ViewportsConfigurationsState> = atomFamily({
  key: 'viewportsConfigurations',
  default: {
    created: Date.now(),
    updated: null,
    data: {},
  },
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect({ version: 2 })],
});

export const generateViewportsConfigurationsSelectorArgs = ({
  caseSmid,
  generatorArgs,
}: Omit<ViewportsConfigurationsSelectorArgs, 'toJSON'>): ViewportsConfigurationsSelectorArgs => ({
  caseSmid,
  generatorArgs,
  toJSON: () => {
    const { defaults, groupedStudies } = generatorArgs;
    return JSON.stringify({
      caseSmid,
      defaults,
      groupedStudies: groupedStudies.map((gs) =>
        gs.map(
          (s) =>
            `${s.smid}:${s.seriesList.map((sl) => sl.smid).join(',')}:${s.stackedFrames
              .map((si) => si.smid)
              .join(',')}`
        )
      ),
    });
  },
});

export const generateViewportConfigurationSelectorArgs = ({
  caseSmid,
  viewportId,
  generatorArgs,
}: Omit<ViewportConfigurationSelectorArgs, 'toJSON'>): ViewportConfigurationSelectorArgs => ({
  caseSmid,
  viewportId,
  generatorArgs,
  toJSON: () => {
    const { defaults, groupedStudies } = generatorArgs;
    return JSON.stringify({
      caseSmid,
      defaults,
      viewportId,
      groupedStudies: groupedStudies.map((gs) =>
        gs.map(
          (s) =>
            `${s.smid}:${s.seriesList.map((sl) => sl.smid).join(',')}:${s.stackedFrames
              .map((si) => si.smid)
              .join(',')}`
        )
      ),
    });
  },
});

/*
 * Same as viewportsConfigurationsState but only returns the actual data
 * and takes care of updating the timestamps when mutations happen.
 * It also allows to pass a nullish case smid since our `useCaseSmid` hook
 * can return such datum.
 */
export const viewportsConfigurationsSelector: (
  args: ViewportsConfigurationsSelectorArgs
) => RecoilState<Nullable<ViewportsConfigurations>> = selectorFamily({
  key: 'viewportsConfigurationsSelector',
  get:
    ({ caseSmid, generatorArgs }) =>
    ({ get }) => {
      if (caseSmid == null) {
        return null;
      }

      const { defaults, groupedStudies } = generatorArgs;

      const isPreviewingHangingProtocol = get(isPreviewingHangingProtocolState);

      // the layoutsForAllCasesState only stores layouts set by the user
      // if there are no layouts set by the user, we use the ones defined
      // in the hanging protocol, if there are no layouts defined in the
      // hanging protocol, we use the default layouts
      // during HP preview, the user-set layout must not filter which HP viewports are populated
      const caseLayouts: Nullable<Layouts> = isPreviewingHangingProtocol
        ? null
        : get(layoutsForAllCasesState)[caseSmid];
      const hangingProtocolLayouts: Nullable<Layouts> = get(hangingProtocolLayoutsState);
      const layouts = caseLayouts ?? hangingProtocolLayouts ?? DEFAULT_LAYOUTS;

      const windows = get(openedViewersState);
      const dehydratedViewportsConfigurations = get(viewportsConfigurationsState(caseSmid))?.data;
      const dehydratedHangingProtocolViewportsConfiguration = get(
        dehydratedHangingProtocol(caseSmid)
      );
      const viewportsConfigurations =
        dehydratedViewportsConfigurations != null
          ? hydrateViewportsConfigurations(dehydratedViewportsConfigurations, groupedStudies)
          : null;
      const hangingProtocolViewportsConfiguration =
        dehydratedHangingProtocolViewportsConfiguration != null
          ? hydrateViewportsConfigurations(
              dehydratedHangingProtocolViewportsConfiguration,
              groupedStudies
            )
          : null;

      return generateViewportsConfigurations({
        defaults,
        groupedStudies,
        layouts,
        windows,
        viewportsConfigurations,
        hangingProtocolViewportsConfiguration,
        isPreviewingHangingProtocol,
      });
    },
  set:
    ({ caseSmid }) =>
    ({ set }, newData: Nullable<DefaultValue | ViewportsConfigurations>) => {
      if (caseSmid == null) return;
      if (newData instanceof DefaultValue) {
        set(viewportsConfigurationsState(caseSmid), (state) => ({
          created: state.created,
          updated: Date.now(),
          data: {},
        }));
      } else {
        const dehydratedData = newData != null ? dehydrateViewportsConfigurations(newData) : null;
        set(viewportsConfigurationsState(caseSmid), (state) => ({
          created: state.created,
          updated: Date.now(),
          data: dehydratedData,
        }));
      }
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

/**
 * This state holds the information needed to adjust the rendered image
 * according to the user-provided settings and the one that have been
 * automatically defined by our logic.
 * It's stored separately and keyed by the case smid and the view type
 * so that we can leave them around and retrieve them later if needed.
 */
type ViewportDisplayConfigurationState = {
  created: Nullable<number>;
  updated: Nullable<number>;
  data: Nullable<ViewportDisplayConfiguration>;
};

// @ts-expect-error [EN-10263] - TS2322 - Type '(param: { stackSmid: string; viewType: ViewportTypeKeys; viewportId: string; }) => RecoilState<{ created: number; updated: null; data: null; }>' is not assignable to type '(arg1: { stackSmid: string; viewType: ViewportTypeKeys; viewportId: string; }) => RecoilState<ViewportDisplayConfigurationState>'.
export const viewportDisplayConfigurationState: (arg1: {
  stackSmid: string;
  viewType: ViewportTypeKeys;
  viewportId: string;
}) => RecoilState<ViewportDisplayConfigurationState> = atomFamily({
  key: 'viewportDisplayConfiguration',
  default: {
    created: Date.now(),
    updated: null,
    data: null,
  },
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
});

const viewportDisplayConfigurationsIdsState = atom<string[]>({
  key: 'viewer.viewportDisplayConfigurationsIdsState',
  default: [],
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
});

export const viewportDisplayHungStacksIdsState: RecoilState<string[]> = atom<string[]>({
  key: 'viewer.dre.viewportDisplayHungStacksIdsState',
  default: [],
  effects: [broadcastChannelSynchronizerEffect({ unidirectional: true })],
});

export const mostRecentMatchingViewportDisplayConfigurationSelector: (arg1: {
  stackSmid: Nullable<string>;
  viewType: Nullable<ViewportTypeKeys>;
}) => RecoilValueReadOnly<Nullable<ViewportDisplayConfiguration>> = selectorFamily({
  key: 'viewer.mostRecentMatchingViewportDisplayConfigurationSelector',
  get:
    ({ stackSmid, viewType }) =>
    ({ get }) => {
      if (stackSmid == null || viewType == null) return null;

      const viewportDisplayConfigurationsIds = get(viewportDisplayConfigurationsIdsState);

      const viewportDisplayConfigurations: ViewportDisplayConfigurationState[] =
        viewportDisplayConfigurationsIds
          .map((viewportId) =>
            get(viewportDisplayConfigurationState({ stackSmid, viewType, viewportId }))
          )
          .filter(Boolean);

      const mostRecentViewportDisplayConfiguration: Nullable<ViewportDisplayConfigurationState> =
        viewportDisplayConfigurations.sort((a, b) => {
          return (b.updated ?? 0) - (a.updated ?? 0);
        })[0];

      return mostRecentViewportDisplayConfiguration?.data;
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const viewportDisplayConfigurationSelector: (
  arg1:
    | {
        stackSmid: Nullable<string>;
        viewType: Nullable<ViewportTypeKeys>;
        viewportId: Nullable<string>;
      }
    | undefined
) => RecoilState<Nullable<ViewportDisplayConfiguration>> = selectorFamily({
  key: 'viewportDisplayConfigurationSelector',
  get:
    (payload) =>
    ({ get }) => {
      const { stackSmid, viewType, viewportId } = payload || {};
      if (stackSmid == null || viewType == null || viewportId == null) return null;
      const exactViewportDisplayConfiguration = get(
        viewportDisplayConfigurationState({ stackSmid, viewType, viewportId })
      ).data;

      let viewportDisplayConfiguration;

      if (exactViewportDisplayConfiguration != null) {
        viewportDisplayConfiguration = exactViewportDisplayConfiguration;
      } else {
        viewportDisplayConfiguration = get(
          mostRecentMatchingViewportDisplayConfigurationSelector({ stackSmid, viewType })
        );
      }

      return viewportDisplayConfiguration;
    },
  set:
    (payload) =>
    ({ set, get }, newData: Nullable<DefaultValue | ViewportDisplayConfiguration>) => {
      const { stackSmid, viewType, viewportId } = payload || {};
      if (stackSmid == null || viewType == null || viewportId == null) return;

      set(viewportDisplayConfigurationsIdsState, (state) =>
        Array.from(new Set([...state, viewportId]))
      );

      if (newData instanceof DefaultValue) {
        set(viewportDisplayConfigurationState({ stackSmid, viewType, viewportId }), (state) => ({
          created: state.created,
          updated: Date.now(),
          data: null,
        }));
      } else {
        set(viewportDisplayConfigurationState({ stackSmid, viewType, viewportId }), (state) => {
          // if the new data is equal to the stored data, we don't need to update
          // the timestamp as the data itself did not change, only exception is
          // when the state is a DefaultValue, in which case we want to update
          // the atom value so that it loses its DefaultValue status
          if (!(state instanceof DefaultValue) && equals(state.data, newData)) {
            return state;
          }

          return {
            created: state.created,
            updated: Date.now(),
            data: newData,
          };
        });
      }
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

/*
 * Here we store the resulting ViewportsConfigurations obtained by resolving
 * the currently selected Hanging Protocol.
 */
export const hangingProtocolViewportConfigurationSelector: (
  arg1: ViewportConfigurationSelectorArgs
) => RecoilValueReadOnly<Nullable<ViewportConfiguration>> = selectorFamily({
  key: 'hangingProtocolViewportConfiguration',
  get:
    ({ caseSmid, viewportId, generatorArgs }) =>
    ({ get }) => {
      const { groupedStudies } = generatorArgs;
      if (caseSmid == null) return null;
      const viewportConfiguration = get(dehydratedHangingProtocol(caseSmid))?.[viewportId];

      if (viewportConfiguration == null) {
        return null;
      }

      analytics.addViewContext(`${globalContext.viewer.viewportConfigurations}.${viewportId}`, {
        seriesSmid: viewportConfiguration?.series?.smid,
        stackSmid: viewportConfiguration?.stack?.smid,
        viewType: viewportConfiguration?.viewType,
      });

      return hydrateViewportConfiguration(viewportConfiguration, groupedStudies);
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

/**
 * Reject incoming broadcast `null` when we already hold a populated value.
 *
 * Why this is needed: even with per-case keying, two viewer windows on the
 * same case share the atom instance via broadcastChannelSynchronizerEffect.
 * Combined with localForagePersisterEffect, a stale `null` can resurrect over
 * a populated atom:
 *   1. Window A populates the HP atom for case X with real data.
 *   2. Window B (secondary viewer, same case X) subscribes later. Its
 *      persister reads a stale `null` from IndexedDB for case X and fires
 *      setSelf(null) locally.
 *   3. The broadcast effect relays window B's null to window A.
 *   4. Window A's HP atom is clobbered; viewports go empty.
 *
 * Applied as the broadcast effect's mergeFn so the null is discarded at the
 * receiver *before* it lands in the local atom — preventing both the local
 * clobber and the downstream re-broadcast that would otherwise ping-pong
 * between windows. reset() / DefaultValue paths do not flow through mergeFn.
 */
export const rejectNullFromBroadcastMergeFn = <T>(
  oldValue: Nullable<T>,
  newValue: Nullable<T>
): Nullable<T> => (newValue == null && oldValue != null ? oldValue : newValue);

export const dehydratedHangingProtocol = atomFamily<
  Nullable<DehydratedViewportsConfigurations>,
  string
>({
  key: 'dehydratedHangingProtocol',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect({ mergeFn: rejectNullFromBroadcastMergeFn }),
    localForagePersisterEffect({
      version: 2,
      __LEGACY_MIGRATION_DO_NOT_USE__disabled: env.NODE_ENV === 'test',
    }),
  ],
});

/*
 * Here we store the layouts that are available for the currently selected Hanging Protocol.
 */
// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<null>' is not assignable to type 'RecoilState<Layouts | null | undefined>'.
export const hangingProtocolLayoutsState: RecoilState<Nullable<Layouts>> = atom({
  key: 'viewer.hangingProtocolLayoutsState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
      __LEGACY_MIGRATION_DO_NOT_USE__disabled: env.NODE_ENV === 'test',
    }),
  ],
});

/*
 * Stores the active hanging protocol SMID.
 * Used by StepBar for clinical step operations (add, reorder) and
 * isDefaultHP detection. Also used for analytics tracking.
 *
 * The merge function rejects stale null echoes from BroadcastChannel.
 * Without this, the atom briefly resets to null when a stale broadcast
 * arrives between the effect setting it and the next render, causing
 * createStepOnBackend and saveStepOrder to silently skip backend calls.
 */
export const activeHangingProtocolSmidState = atom<string | null>({
  key: 'viewer.activeHangingProtocolSmidState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect({
      mergeFn: (oldValue: Nullable<string>, newValue: string | null) => {
        // Reject a null incoming value when local has one (stale echo). When the
        // incoming value is null and local is also null the result is the same,
        // so returning oldValue covers both cases (normalizing undefined to null
        // to satisfy the atom's string | null type). Otherwise accept the incoming.
        return newValue == null ? (oldValue ?? null) : newValue;
      },
    }),
  ],
});

/**
 * Here we store the resulting ViewportsConfigurations obtained by resolving
 * the currently previewed Hanging Protocol.
 */
// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<null>' is not assignable to type 'RecoilState<ViewportsConfigurations | null | undefined>'.
export const hangingProtcolPreviewState: RecoilState<Nullable<ViewportsConfigurations>> = atom({
  key: 'hangingProtcolPreviewState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
      __LEGACY_MIGRATION_DO_NOT_USE__disabled: env.NODE_ENV === 'test',
    }),
  ],
});

/**
 * This selector allows each viewport to retrieve its own configuration and update it.
 * The selector takes care of merging the new configuration with the rest of the viewer configurations.
 */
export const viewportConfigurationSelector: (
  arg1: ViewportConfigurationSelectorArgs
) => RecoilState<Nullable<ViewportConfiguration>> = selectorFamily({
  key: 'viewportConfiguration',
  get:
    ({ caseSmid, viewportId, generatorArgs }) =>
    ({ get }) => {
      const viewportConfiguration = get(
        viewportsConfigurationsSelector(
          generateViewportsConfigurationsSelectorArgs({ caseSmid, generatorArgs })
        )
      )?.[viewportId];

      analytics.addViewContext(`${globalContext.viewer.viewportConfigurations}.${viewportId}`, {
        seriesSmid: viewportConfiguration?.series?.smid,
        stackSmid: viewportConfiguration?.stack?.smid,
        viewType: viewportConfiguration?.viewType,
      });

      return viewportConfiguration;
    },
  set:
    ({ caseSmid, viewportId, generatorArgs }) =>
    ({ get, set }, viewportConfiguration: Nullable<DefaultValue | ViewportConfiguration>) => {
      if (caseSmid == null) {
        console.warn(
          'viewportConfigurationSelector: caseSmid is required while setting a viewport configuration!'
        );
        return;
      }

      const isPreviewing = get(isPreviewingHangingProtocolState);

      // Users are not allowed to make changes to the viewports while previewing a hanging protocol.
      // If they tried to do so, it would be impossible to reset the viewports configurations state
      // to the original state.
      if (isPreviewing) return;

      set(
        // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'RecoilState<ViewportsConfigurations | null | undefined>' is not assignable to parameter of type 'RecoilState<ViewportsConfigurations>'.
        viewportsConfigurationsSelector(
          generateViewportsConfigurationsSelectorArgs({ caseSmid, generatorArgs })
        ),
        (state: ViewportsConfigurations) =>
          ({
            ...state,
            [viewportId]: viewportConfiguration,
          }) as unknown as ViewportsConfigurations
      );
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS: Partial<ViewportConfiguration> = {
  viewType: ViewType.TwoDDre,
};

/**
 * This hook is useful to retrieve the whole viewports configurations configuration.
 *
 * Usage:
 * ```
 * const viewportsConfigurations = useRecoilValue(useViewportsConfigurationsSelector());
 * ```
 */
export function useViewportsConfigurationsSelector({
  defaults = VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
  context,
}: {
  defaults?: Partial<ViewportConfiguration>;
} & WithApolloContext): RecoilState<Nullable<ViewportsConfigurations>> {
  const currentCaseId = useCurrentCaseId();
  const { groupedStudies } = useStudies({ context });

  return useMemo(
    () =>
      viewportsConfigurationsSelector(
        generateViewportsConfigurationsSelectorArgs({
          caseSmid: currentCaseId ?? NOT_FOUND_CASE_ID,
          generatorArgs: { defaults, groupedStudies },
        })
      ),
    [currentCaseId, defaults, groupedStudies]
  );
}

type GetViewportConfigurationSelectorProps = {
  viewportId: string;
  defaults?: Partial<ViewportConfiguration>;
  groupedStudies: Array<StudyGroup>;
  currentCaseId: Nullable<string>;
};

export function getViewportConfigurationSelector({
  viewportId,
  defaults = VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
  groupedStudies,
  currentCaseId,
}: GetViewportConfigurationSelectorProps): RecoilState<Nullable<ViewportConfiguration>> {
  return viewportConfigurationSelector(
    generateViewportConfigurationSelectorArgs({
      caseSmid: currentCaseId,
      viewportId,
      generatorArgs: {
        defaults: { ...VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS, ...defaults },
        groupedStudies,
      },
    })
  );
}

export function getHPViewportConfigurationSelector({
  viewportId,
  groupedStudies,
  currentCaseId,
}: GetViewportConfigurationSelectorProps): RecoilValueReadOnly<Nullable<ViewportConfiguration>> {
  return hangingProtocolViewportConfigurationSelector(
    generateViewportConfigurationSelectorArgs({
      caseSmid: currentCaseId,
      viewportId,
      generatorArgs: {
        groupedStudies,
      },
    })
  );
}

export function useHPViewportConfigurationSelector({
  viewportId,
  context,
}: {
  viewportId: string;
} & WithApolloContext): RecoilValueReadOnly<Nullable<ViewportConfiguration>> {
  const currentCaseId = useCurrentCaseId();
  const { groupedStudies } = useStudies({ context });

  return useMemo(
    () => getHPViewportConfigurationSelector({ viewportId, groupedStudies, currentCaseId }),
    [currentCaseId, groupedStudies, viewportId]
  );
}

/**
 * This hook is useful to retrieve a specific viewport configuration ensuring
 * the correct default configurations are generated starting from the currently
 * loaded studies.
 *
 * Usage:
 * ```
 * const viewportConfiguration = useRecoilValue(useViewportConfigurationSelector({ viewportId: 'Viewport-0-0-0' }));
 * ```
 */
export function useViewportConfigurationSelector({
  viewportId,
  defaults = VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
  context,
}: {
  viewportId: string;
  defaults?: Partial<ViewportConfiguration>;
} & WithApolloContext): RecoilState<Nullable<ViewportConfiguration>> {
  const currentCaseId = useCurrentCaseId();
  const { groupedStudies } = useStudies({ context });

  return useMemo(
    () => getViewportConfigurationSelector({ viewportId, defaults, groupedStudies, currentCaseId }),
    [currentCaseId, defaults, groupedStudies, viewportId]
  );
}

/**
 * Same as `useViewportConfigurationSelector` but automatically returns the
 * guide viewport configuration selector instead of asking for a viewport id.
 */
export function useGuideViewportConfigurationSelector({
  defaults = {},
  context,
}: {
  defaults?: Partial<ViewportConfiguration>;
} & WithApolloContext): RecoilState<Nullable<ViewportConfiguration>> {
  const viewportId = useRecoilValue(activeViewportState);
  return useViewportConfigurationSelector({
    viewportId,
    defaults,
    context,
  });
}

/**
 * This hook is useful to retrieve a specific viewport display configuration.
 *
 * Usage:
 * ```
 * const viewportDisplayConfiguration = useRecoilValue(useViewportDisplayConfigurationSelector({ viewportId: 'Viewport-0-0-0' }));
 * ```
 */
export function useViewportDisplayConfigurationSelector({
  viewportId,
  context,
}: {
  viewportId: string;
} & WithApolloContext): RecoilState<Nullable<ViewportDisplayConfiguration>> {
  const viewportConfiguration = useRecoilValue(
    useViewportConfigurationSelector({ viewportId, context })
  );

  return useMemo(
    () =>
      viewportDisplayConfigurationSelector({
        stackSmid: viewportConfiguration?.stack?.smid,
        viewType: viewportConfiguration?.viewType,
        viewportId,
      }),
    [viewportConfiguration?.stack?.smid, viewportConfiguration?.viewType, viewportId]
  );
}

/**
 * Same as `useViewportDisplayConfigurationSelector` but automatically returns the
 * guide viewport display configuration selector instead of asking for a viewport id.
 */
export function useGuideViewportDisplayConfigurationSelector({
  context,
}: WithApolloContext): RecoilState<Nullable<ViewportDisplayConfiguration>> {
  const viewportId = useRecoilValue(activeViewportState);
  const viewportConfiguration = useRecoilValue(useGuideViewportConfigurationSelector({ context }));
  return viewportDisplayConfigurationSelector({
    stackSmid: viewportConfiguration?.stack?.smid,
    viewType: viewportConfiguration?.viewType,
    viewportId,
  });
}

// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<never[]>' is not assignable to type 'RecoilState<VtkAnnotation[]>'.
export const annotationsCacheState: RecoilState<Array<VtkAnnotation>> = atom({
  key: 'annotationsCacheState',
  default: [],
  effects: [broadcastChannelSynchronizerEffect({ unidirectional: true })],
});

export const segmentsForAnnotationSelector: (
  annotationId?: Nullable<string>
) => RecoilValueReadOnly<ReadonlyArray<Segment>> = selectorFamily({
  key: 'segmentsForAnnotationByIdSelector',
  get:
    (annotationId?: string | null) =>
    ({ get }) => {
      const annotations = get(annotationsCacheState);
      return annotations.find((annotation) => annotation.id === annotationId)?.segments ?? [];
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const annotationForIdSelector: (
  annotationId: Nullable<string>
) => RecoilValueReadOnly<Nullable<VtkAnnotation>> = selectorFamily({
  key: 'annotationForId',
  get:
    (annotationId: Nullable<string>) =>
    ({ get }) => {
      const annotations = get(annotationsCacheState);
      return annotations.find((annotation) => annotation.id === annotationId);
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const annotationsForSeriesSmids: (
  seriesSmids: string[]
) => RecoilValueReadOnly<VtkAnnotation[]> = selectorFamily({
  key: 'annotationsForSeriesSmids',
  get:
    (seriesSmids: string[]) =>
    ({ get }) => {
      const annotations = get(annotationsCacheState);

      // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      return annotations.filter((annotation) => seriesSmids.includes(annotation.seriesSmid));
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const studySuvParameters: (
  seriesSmids: string[]
) => RecoilValueReadOnly<Nullable<ScaleFactorTags>> = selectorFamily({
  key: 'studySuvParameters',
  get:
    (seriesSmids: string[]) =>
    ({ get }) => {
      const annotationsForStudy = get(annotationsForSeriesSmids(seriesSmids));

      return annotationsForStudy.find((annotation) => annotation.suv != null)?.suv;
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});
export const presentationStateAnnotationExistsForListOfSeriesSmidsSelector: (params: {
  seriesSmids?: ReadonlyArray<string>;
  sopInstanceUID?: string;
}) => RecoilValueReadOnly<boolean> = selectorFamily({
  key: 'presentationStateAnnotationExistsForListOfSeriesSmidsSelector',
  get:
    (params: { seriesSmids?: ReadonlyArray<string>; sopInstanceUID?: string }) =>
    ({ get }) => {
      if (params.seriesSmids == null || params.seriesSmids.length === 0) return false;
      const annotations = get(annotationsCacheState);
      const hasAnnotation = annotations
        .filter((annotation) =>
          params.sopInstanceUID != null
            ? annotation.metadata.sopInstanceUID === params.sopInstanceUID
            : true
        )
        .some(
          (annotation) =>
            // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
            params.seriesSmids.includes(annotation.seriesSmid) && annotation.source === 'GSPS'
        );
      return hasAnnotation;
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const usePresentationStateAnnotationExistsForListOfSeriesSmids = (
  seriesSmids?: ReadonlyArray<string>,
  sopInstanceUID?: string
): boolean => {
  const selector = presentationStateAnnotationExistsForListOfSeriesSmidsSelector({
    seriesSmids,
    sopInstanceUID,
  });
  const annotationsExist = useRecoilValue(selector);
  return annotationsExist;
};
